import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search } from 'lucide-react';
import { apiClient } from '../../utils/apiHelper';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SuppliersLedger = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [selectedSupplier, setSelectedSupplier] = useState('');
  const [ledger, setLedger] = useState(null);
  const [loading, setLoading] = useState(false);

  React.useEffect(() => { fetchSuppliers(); }, []);

  const fetchSuppliers = async () => {
    try {
      const response = await apiClient.get('/api/accounts/suppliers'); setSuppliers(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!selectedSupplier) return;
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/accounts/supplier-ledger/${selectedSupplier}`);
      if (response.status === 200 || response.data) {
        setLedger(await response.json());
      } else {
        alert('Supplier not found');
      }
    } catch (error) {
      alert('Failed to fetch ledger');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <Card>
        <CardHeader><CardTitle>Supplier Ledger</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={handleSearch} className="flex gap-4 items-end mb-6">
            <div className="flex-1">
              <Label>Select Supplier *</Label>
              <Select value={selectedSupplier} onValueChange={setSelectedSupplier}>
                <SelectTrigger><SelectValue placeholder="Select supplier" /></SelectTrigger>
                <SelectContent>
                  {suppliers.map((s) => (
                    <SelectItem key={s.id} value={s.id}>{s.supplier_name} ({s.company_name})</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={loading}>
              <Search className="w-4 h-4 mr-2" />{loading ? 'Searching...' : 'Search'}
            </Button>
          </form>

          {ledger && (
            <>
              <Card className="mb-6">
                <CardHeader><CardTitle className="text-lg">Supplier Information</CardTitle></CardHeader>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div><p className="text-sm text-gray-500">Supplier Name</p><p className="font-medium">{ledger.supplier.supplier_name}</p></div>
                    <div><p className="text-sm text-gray-500">Company</p><p className="font-medium">{ledger.supplier.company_name || '-'}</p></div>
                    <div><p className="text-sm text-gray-500">Phone</p><p className="font-medium">{ledger.supplier.phone}</p></div>
                    <div><p className="text-sm text-gray-500">Email</p><p className="font-medium">{ledger.supplier.email || '-'}</p></div>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-red-50 rounded-lg">
                  <p className="text-sm text-gray-600">Total Expenses</p>
                  <p className="text-2xl font-bold text-red-600">₹{ledger.summary.total_expenses.toFixed(2)}</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <p className="text-sm text-gray-600">Total Payments</p>
                  <p className="text-2xl font-bold text-green-600">₹{ledger.summary.total_payments.toFixed(2)}</p>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg">
                  <p className="text-sm text-gray-600">Balance Due</p>
                  <p className="text-2xl font-bold text-orange-600">₹{ledger.summary.balance.toFixed(2)}</p>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold mb-3">Expenses</h3>
                  <table className="w-full border-collapse">
                    <thead><tr className="border-b"><th className="text-left p-3">Date</th><th className="text-left p-3">Category</th><th className="text-left p-3">Amount</th><th className="text-left p-3">Description</th></tr></thead>
                    <tbody>
                      {ledger.expenses.length === 0 ? <tr><td colSpan="4" className="text-center p-4 text-gray-500">No expenses</td></tr> : ledger.expenses.map((exp, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="p-3">{exp.expense_date}</td>
                          <td className="p-3">{exp.category}</td>
                          <td className="p-3 text-red-600">₹{exp.amount}</td>
                          <td className="p-3">{exp.description}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                <div>
                  <h3 className="text-lg font-semibold mb-3">Payments</h3>
                  <table className="w-full border-collapse">
                    <thead><tr className="border-b"><th className="text-left p-3">Payment #</th><th className="text-left p-3">Date</th><th className="text-left p-3">Amount</th><th className="text-left p-3">Mode</th></tr></thead>
                    <tbody>
                      {ledger.payments.length === 0 ? <tr><td colSpan="4" className="text-center p-4 text-gray-500">No payments</td></tr> : ledger.payments.map((pay, idx) => (
                        <tr key={idx} className="border-b hover:bg-gray-50">
                          <td className="p-3">{pay.payment_number}</td>
                          <td className="p-3">{pay.payment_date}</td>
                          <td className="p-3 text-green-600">₹{pay.amount}</td>
                          <td className="p-3">{pay.payment_mode}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default SuppliersLedger;